/**
 * NiV tool — manually (re)generate a clinic's monthly report via the
 * generateMonthlyReport Cloud Function. Error code: CK-9890.
 */

import { useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { CalendarCheck, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import { functions } from "../../../../firebase";
import { useGetNivClinicsOverviewQuery } from "../../../../store/services/nivApi";
import { useToast } from "../../../../contexts/ToastContext";

const HEBREW_MONTHS = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];

const israelNow = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
  })
    .format(new Date())
    .split("-");
  return { year: Number(parts[0]), month: Number(parts[1]) };
};

function NivMonthlyReportTool({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const { data: allRows = [] } = useGetNivClinicsOverviewQuery(undefined, {
    skip: !isAuthorized,
  });
  const clinics = useMemo(
    () =>
      allRows
        .filter((r) => !r.inactiveByAdmin)
        .slice()
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "he")),
    [allRows]
  );

  const now = israelNow();
  // Default to the previous month (the one a report normally covers)
  const defaultYear = now.month === 1 ? now.year - 1 : now.year;
  const defaultMonth = now.month === 1 ? 12 : now.month - 1;

  const [clinicId, setClinicId] = useState("");
  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!clinicId || !year || !month) return;
    setBusy(true);
    setResult(null);
    try {
      const generate = httpsCallable(functions, "generateMonthlyReport");
      const res = await generate({
        clinicId,
        year: Number(year),
        month: Number(month),
        notify: true,
      });
      setResult({ ok: true, ...res.data });
      showSuccess(`הדוח נוצר בהצלחה (${res.data.monthKey})`);
    } catch (error) {
      console.error("[CK-9890] Manual monthly report generation failed:", {
        errorCode: "CK-9890",
        errorMessage: error?.message,
        clinicId,
        year,
        month,
        timestamp: new Date().toISOString(),
      });
      setResult({ ok: false, message: error?.message || "שגיאה לא צפויה" });
      showError("שגיאה ביצירת הדוח");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="niv-report-content">
      <div className="niv-report-header">
        <h3>
          <CalendarCheck size={22} weight="fill" />
          יצירת דוח חודשי ידנית
        </h3>
      </div>
      <p className="niv-report-help">
        בחירת קליניקה ושנה/חודש ולחיצה על "צור דוח" מייצרת את הדוח מחדש. דוח קיים יידרס.
        תישלח הודעת Push ו-In-App לכל המטפלים הפעילים בקליניקה.
      </p>

      <div className="niv-report-form">
        <div className="niv-report-form-row">
          <label className="niv-push-label" htmlFor="niv-report-clinic">קליניקה *</label>
          <select
            id="niv-report-clinic"
            className="niv-push-input"
            value={clinicId}
            onChange={(e) => setClinicId(e.target.value)}
            disabled={busy}
          >
            <option value="">— בחר/י קליניקה —</option>
            {clinics.map((c) => (
              <option key={c.clinicId} value={c.clinicId}>
                {(c.name || "ללא שם") + (c.ownerName ? ` — ${c.ownerName}` : "")}
              </option>
            ))}
          </select>
        </div>

        <div className="niv-report-form-grid">
          <div className="niv-report-form-row">
            <label className="niv-push-label" htmlFor="niv-report-year">שנה *</label>
            <select
              id="niv-report-year"
              className="niv-push-input"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              disabled={busy}
            >
              {Array.from({ length: 6 }, (_, i) => now.year - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="niv-report-form-row">
            <label className="niv-push-label" htmlFor="niv-report-month">חודש *</label>
            <select
              id="niv-report-month"
              className="niv-push-input"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              disabled={busy}
            >
              {HEBREW_MONTHS.map((label, idx) => (
                <option key={idx + 1} value={idx + 1}>{`${idx + 1} — ${label}`}</option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          className="niv-btn niv-btn-primary niv-report-submit"
          onClick={handleGenerate}
          disabled={busy || !clinicId}
        >
          {busy ? (
            <>
              <LoaderLoop size="xs" /> מייצר דוח...
            </>
          ) : (
            <>
              <CalendarCheck size={18} weight="fill" /> צור דוח
            </>
          )}
        </button>

        {result?.ok && (
          <div className="niv-report-result success">
            <CheckCircle size={18} weight="fill" />
            <span>
              הדוח נוצר: <strong>{result.monthKey}</strong>
              {result.reportPath && (
                <>
                  {" "}— <code>{result.reportPath}</code>
                </>
              )}
            </span>
          </div>
        )}
        {result && !result.ok && (
          <div className="niv-report-result error">
            <WarningCircle size={18} weight="fill" />
            <span>{result.message}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default NivMonthlyReportTool;
